import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import './Applicants.css';
import Dashboard from './Dashboard.js';
import { useCompany } from '../context/CompanyContext';

const Applicants = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalApplicants, setTotalApplicants] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Enhanced filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    applicationStage: [],
    hiringStage: [],
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Enhanced date filter state
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    display: 'Select Date Range'
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();

  const fetchApplicants = async (companyId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      // Add filters to params
      if (filters.applicationStage.length > 0) {
        params.append('application_status', filters.applicationStage.join(','));
      }
      if (filters.hiringStage.length > 0) {
        params.append('hiring_stage', filters.hiringStage.join(','));
      }
      if (filters.search.trim()) {
        params.append('query', filters.search.trim());
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-applications/list-company-applicates/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplicants(data.items || []);
        setTotalApplicants(data.total || 0);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch applicants');
      }
    } catch (error) {
      setError('Failed to fetch applicants');
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchApplicants(selectedCompany.company_id);
    }
  }, [currentPage, pageSize, selectedCompany, filters]);

  // Filter handlers
  const handleFilterChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterType === 'search') {
        newFilters.search = value;
      } else {
        if (checked) {
          newFilters[filterType] = [...prev[filterType], value];
        } else {
          newFilters[filterType] = prev[filterType].filter(item => item !== value);
        }
      }
      
      return newFilters;
    });
    
    setCurrentPage(1);
  };

  // Date filter handlers
  const handleDateRangeChange = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: startDate,
      dateTo: endDate
    }));
    
    // Update display
    if (startDate && endDate) {
      const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setDateRange({
        startDate,
        endDate,
        display: `${start} - ${end}`
      });
    }
    
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  // Predefined date ranges
  const applyDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (range) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7days':
        endDate = today.toISOString().split('T')[0];
        startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case 'last30days':
        endDate = new Date().toISOString().split('T')[0];
        startDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    handleDateRangeChange(startDate, endDate);
  };

  const clearAllFilters = () => {
    setFilters({
      applicationStage: [],
      hiringStage: [],
      search: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setDateRange({
      startDate: '',
      endDate: '',
      display: 'Select Date Range'
    });
    setCurrentPage(1);
  };

  const getActiveFilterCount = () => {
    return filters.applicationStage.length + 
           filters.hiringStage.length + 
           (filters.search ? 1 : 0) + 
           (filters.dateFrom && filters.dateTo ? 1 : 0);
  };

  // Enhanced badge functions
  const getApplicationStageBadge = (stage) => {
    switch (stage.toLowerCase()) {
      case 'offered':
        return 'app-stage-offered';
      case 'active':
        return 'app-stage-active';
      case 'pending':
        return 'app-stage-pending';
      case 'rejected':
        return 'app-stage-rejected';
      case 'cancelled':
        return 'app-stage-cancelled';
      case 'withdrawn':
        return 'app-stage-withdrawn';
      default:
        return 'app-stage-default';
    }
  };

  const getHiringStageBadge = (stage) => {
    switch (stage.toLowerCase()) {
      case 'offer':
        return 'hiring-stage-offer';
      case 'submitted':
        return 'hiring-stage-submitted';
      case 'interview':
        return 'hiring-stage-interview';
      case 'rejected':
        return 'hiring-stage-rejected';
      case 'test':
        return 'hiring-stage-test';
      case 'shortlisted':
        return 'hiring-stage-shortlisted';
      case 'final':
        return 'hiring-stage-final';
      default:
        return 'hiring-stage-default';
    }
  };

  // Score badge based on value
  const getScoreBadge = (score) => {
    const num = parseFloat(score) || 0;
    if (num >= 4.0) return 'score-excellent';
    if (num >= 3.0) return 'score-good';
    if (num >= 2.0) return 'score-average';
    if (num > 0) return 'score-poor';
    return 'score-none';
  };

  // Days since applied
  const getDaysSinceApplied = (dateString) => {
    const applied = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - applied);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  const formatScore = (score) => {
    return score > 0 ? score.toFixed(1) : '0.0';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleSeeApplication = (applicantId) => {
    navigate(`/application-detail/${applicantId}`);
  };

  const totalPages = Math.ceil(totalApplicants / pageSize);

  return (
    <Dashboard page={"application"}>
      <div className="applicants-listing-container">
        <div className="applicants-content">
          <div className="content-header">
            <div className="header-info">
              <h1>Total Applicants: {totalApplicants}</h1>
              <p className="applicants-summary">
                Showing {applicants.length} of {totalApplicants} applicants
                {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''} applied)`}
              </p>
            </div>
            <div className="content-actions">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search Applicants"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    handleFilterChange('search', e.target.value);
                  }}
                  className="search-input"
                />
                <span className="search-icon">🔍</span>
              </div>
              
              <button 
                className="filter-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="filter-icon">⚙️</span>
                Filter
                {getActiveFilterCount() > 0 && (
                  <span className="filter-count">{getActiveFilterCount()}</span>
                )}
              </button>

              <div className="date-range-selector">
                <button 
                  className="date-range-btn"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  {dateRange.display} 📅
                </button>
                
                {showDatePicker && (
                  <div className="date-picker-dropdown">
                    <div className="date-picker-header">
                      <h4>Select Date Range</h4>
                      <button 
                        onClick={() => setShowDatePicker(false)}
                        className="close-picker-btn"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="date-picker-presets">
                      <button onClick={() => applyDateRange('today')}>Today</button>
                      <button onClick={() => applyDateRange('yesterday')}>Yesterday</button>
                      <button onClick={() => applyDateRange('last7days')}>Last 7 Days</button>
                      <button onClick={() => applyDateRange('last30days')}>Last 30 Days</button>
                      <button onClick={() => applyDateRange('thisMonth')}>This Month</button>
                    </div>
                    
                    <div className="date-picker-custom">
                      <h5>Custom Range</h5>
                      <div className="date-inputs">
                        <input
                          type="date"
                          value={dateRange.startDate}
                          onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                          className="date-input"
                        />
                        <span>to</span>
                        <input
                          type="date"
                          value={dateRange.endDate}
                          onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                          className="date-input"
                        />
                      </div>
                      <button 
                        onClick={() => handleDateRangeChange(dateRange.startDate, dateRange.endDate)}
                        disabled={!dateRange.startDate || !dateRange.endDate}
                        className="apply-date-btn"
                      >
                        Apply Range
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Enhanced Filters Panel */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filters-content">
                <div className="filter-group">
                  <h4>Application Stage</h4>
                  <div className="filter-options">
                    {['offered', 'active', 'pending', 'rejected', 'cancelled', 'withdrawn'].map(stage => (
                      <label key={stage} className="filter-option">
                        <input
                          type="checkbox"
                          checked={filters.applicationStage.includes(stage)}
                          onChange={(e) => handleFilterChange('applicationStage', stage, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        <span className="option-label">{stage.charAt(0).toUpperCase() + stage.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <h4>Hiring Stage</h4>
                  <div className="filter-options">
                    {['submitted', 'interview', 'test', 'decision'].map(stage => (
                      <label key={stage} className="filter-option">
                        <input
                          type="checkbox"
                          checked={filters.hiringStage.includes(stage)}
                          onChange={(e) => handleFilterChange('hiringStage', stage, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        <span className="option-label">{stage.charAt(0).toUpperCase() + stage.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="filter-actions">
                  <button onClick={clearAllFilters} className="clear-filters-btn">
                    Clear All
                  </button>
                  <button onClick={() => setShowFilters(false)} className="apply-filters-btn">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {getActiveFilterCount() > 0 && (
            <div className="active-filters-display">
              <div className="active-filters-header">
                <span>Active Filters:</span>
                <button onClick={clearAllFilters} className="clear-all-quick-btn">Clear All</button>
              </div>
              <div className="active-filters-list">
                {filters.applicationStage.map(stage => (
                  <span key={stage} className="filter-chip">
                    App Stage: {stage.charAt(0).toUpperCase() + stage.slice(1)}
                    <button 
                      onClick={() => handleFilterChange('applicationStage', stage, false)}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {filters.hiringStage.map(stage => (
                  <span key={stage} className="filter-chip">
                    Hiring: {stage.charAt(0).toUpperCase() + stage.slice(1)}
                    <button 
                      onClick={() => handleFilterChange('hiringStage', stage, false)}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {filters.search && (
                  <span className="filter-chip">
                    Search: "{filters.search}"
                    <button 
                      onClick={() => {
                        setSearchTerm('');
                        handleFilterChange('search', '');
                      }}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.dateFrom && filters.dateTo && (
                  <span className="filter-chip">
                    Date: {dateRange.display}
                    <button 
                      onClick={() => {
                        setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
                        setDateRange({ startDate: '', endDate: '', display: 'Select Date Range' });
                      }}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

        <div className="main-applicants-table">
          <div className="table-header application">
            <div className="header-cell name-header">Applicant</div>
            <div className="header-cell score-header">Score</div>
            <div className="header-cell stage-header">App Stage</div>
            <div className="header-cell stage-header">Hiring Stage</div>
            <div className="header-cell date-header">Applied</div>
            <div className="header-cell role-header">Job Role</div>
          </div>

          <div className="table-body">
            {loading ? (
              <div className="loading-state">Loading applicants...</div>
            ) : applicants.length > 0 ? (
              applicants.map((applicant) => (
                <div 
                  onClick={() => handleSeeApplication(applicant.application_id)} 
                  key={applicant.application_id} 
                  className="table-row application"
                >
                  <div className="table-cell name-cell">
                    <div className="applicant-info">
                      <div className="applicant-name-container">
                        <div className="avatar">
                          <img 
                            src={`${process.env.REACT_APP_API_URL}/profile/get-pic/${encodeURIComponent(applicant.user_id)}`} 
                            alt={applicant.user_names}
                            onError={(e) => {
                              e.target.src = '/default-avatar.png';
                            }}
                          />
                        </div>
                        <span className="applicant-name">{applicant.user_names}</span>
                      </div>
                      <span className="applied-time">{getDaysSinceApplied(applicant.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="table-cell score-cell">
                    <div className="score-info">
                      <div className="score">
                        <span className="star">⭐</span>
                        <span className={`score-value ${getScoreBadge(0)}`}>{formatScore(0)}</span>
                      </div>
                      <span className="score-label">rating</span>
                    </div>
                  </div>
                  
                  <div className="table-cell stage-cell">
                    <span className={`stage-badge ${getApplicationStageBadge(applicant.status)}`}>
                      {applicant.status}
                    </span>
                  </div>
                  
                  <div className="table-cell stage-cell">
                    <span className={`stage-badge ${getHiringStageBadge(applicant.stages)}`}>
                      {applicant.stages}
                    </span>
                  </div>
                  
                  <div className="table-cell date-cell">
                    <span className="formatted-date">{formatDate(applicant.created_at)}</span>
                  </div>
                  
                  <div className="table-cell role-cell">
                    <span className="job-title">{applicant.job_title}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                No applicants found matching your filters.
                <button onClick={clearAllFilters} className="clear-filters-suggestion">
                  Clear filters to see all applicants
                </button>
              </div>
            )}
          </div>
        </div>

          <div className="pagination">
            <div className="pagination-info">
              <span>View</span>
              <select 
                value={pageSize} 
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="page-size-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>Applicants per page</span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default Applicants;